import React from 'react';
import { Box, ClassNameMap } from '@mui/material';
import { useHistory } from 'react-router';
import {
  ContactSupportOutlined,
  Contacts, ContactsOutlined, People, PeopleOutlined,
  DateRange, DateRangeOutlined,
  MonetizationOn, MonetizationOnOutlined,
  SettingsOutlined,
  AccountBoxOutlined,
  PersonAddOutlined,
  LocationCityOutlined,
  DescriptionOutlined,
} from '@mui/icons-material';
import { providerPatientRoute, ROUTING_CONFIG } from '@uplift/constants';

import {
  ContactSupportIcon,
  HomeIcon, HomeOutlinedIcon,
} from '@uplift/icons';
import { PatientsSearch } from '../../PatientSearch';
import { StickyStackItem } from '../../../StickyStack';
import { Navigation, NavigationButton, NavigationLink, NavigationLinks, NavigationLogo, NavigationProfileMenu, NavigationProfileMenuItem, NavigationProfileMenuItemLink, NavigationProps, NavigationSection } from '../../../components';

import EmailIcon from '@mui/icons-material/EmailOutlined';
import { useMessagingContext } from '../../../../Messaging';
import { SignOnBonusReferralSource } from '@uplift/api';
import { ReferProviderModal } from './ReferProviderModal.component';
import { useCanAccessPracticeStates } from '../../../../PracticeStates/hooks';
import { useFeatureFlag, useL10n } from '@uplift/hooks';

export type ProviderDesktopNavigationProps = {
  logoLink?: string;
  avatarSrc?: string;
  providerId?: string;
  classes?: ClassNameMap<'root'>;
  variant?: NavigationProps['variant'];
  showClientsSearch?: boolean;
  showLinks?: boolean;
  showProfile?: boolean;
  showProfileLinks?: boolean;
  showFinanceLink?: boolean;
  showSuperviseesLink?: boolean;
  showMessages?: boolean;
  overrideStart?: React.ReactNode;
};


export const ProviderDesktopNavigation: React.FC<ProviderDesktopNavigationProps> = ({
  avatarSrc,
  providerId,
  logoLink = ROUTING_CONFIG.main,
  showClientsSearch = true,
  showLinks = true,
  showProfile = true,
  showProfileLinks = true,
  showFinanceLink = true,
  showSuperviseesLink = false,
  showMessages = true,
  overrideStart = null,
  ...props
}) => {
  const l10n = useL10n();
  const history = useHistory();
  const messaging = useMessagingContext();
  const PROVIDER_DOCUMENT_CENTER = useFeatureFlag('PROVIDER_DOCUMENT_CENTER');
  const [ referModalOpen, setReferModalOpen ] = React.useState(false);
  const { canAccess: canAccessPracticeStates } = useCanAccessPracticeStates();

  const profileLinks = [
    <NavigationProfileMenuItemLink key="account-settings" label="Account Settings" path={ROUTING_CONFIG.settings} icon={SettingsOutlined} />,
    <NavigationProfileMenuItemLink key="profile-settings" label="Matching Profile" path={ROUTING_CONFIG.profile} icon={AccountBoxOutlined} />,
  ];

  if (canAccessPracticeStates) {
    profileLinks.push(<NavigationProfileMenuItemLink key="practice-states" label="Practice States" path={ROUTING_CONFIG.practiceStates} icon={LocationCityOutlined} />);
  }

  if (PROVIDER_DOCUMENT_CENTER) {
    profileLinks.push(<NavigationProfileMenuItemLink key="documents-center" label="Documents Center" path={ROUTING_CONFIG.documentsCenter} icon={DescriptionOutlined} />);
  }

  profileLinks.push(<NavigationProfileMenuItem key="refer-provider" label="Refer a Provider" icon={PersonAddOutlined} onClick={() => setReferModalOpen(true)} />);

  return (
    <>
      <StickyStackItem order={0} placement="top">
        <Navigation position="sticky" variant={props.variant}>
          <NavigationSection alignment="start">
            {overrideStart ?? <NavigationLogo href={logoLink} />}
          </NavigationSection>

          <NavigationSection alignment="center" vertical="center">
            {showClientsSearch && (
              <Box width="100%" maxWidth="400px">
                <PatientsSearch
                  onPatientSelect={patient => {
                    if (patient) {
                      history.push(providerPatientRoute.createLink({ patientId: patient.id }));
                    }
                  }}
                />
              </Box>
            )}
          </NavigationSection>

          <NavigationSection alignment="end">
            {showLinks && (
              <NavigationLinks>
                <NavigationLink
                  title={l10n.pages.main}
                  icon={HomeOutlinedIcon}
                  iconActive={HomeIcon}
                  path={ROUTING_CONFIG.main}
                />
                <NavigationLink
                  title={l10n.schedule.createAppointment.schedule}
                  icon={DateRangeOutlined}
                  iconActive={DateRange}
                  path={ROUTING_CONFIG.schedule}
                />
                {showFinanceLink && (
                  <NavigationLink
                    title={l10n.finance.title}
                    icon={MonetizationOnOutlined}
                    iconActive={MonetizationOn}
                    path={ROUTING_CONFIG.finance}
                  />
                )}
                <NavigationLink
                  title={l10n.pages.clients}
                  icon={ContactsOutlined}
                  iconActive={Contacts}
                  path={ROUTING_CONFIG.patients}
                  exact
                />
                {showSuperviseesLink && (
                  <NavigationLink
                    title="Supervisees"
                    icon={PeopleOutlined}
                    iconActive={People}
                    path={ROUTING_CONFIG.supervisees}
                    exact
                  />
                )}
                <NavigationLink
                  title={l10n.pages.contactSupport}
                  icon={ContactSupportOutlined}
                  iconActive={ContactSupportIcon}
                  path={ROUTING_CONFIG.support}
                />
                {showMessages ? (
                  <NavigationButton
                    title={l10n.pages.messages}
                    icon={EmailIcon}
                    iconActive={EmailIcon}
                    badge={messaging.unreadMessagesCount}
                    onClick={() => {
                      messaging.openModal();
                    }}
                  />
                ) : null}
              </NavigationLinks>
            )}
            {showProfile && (
              <NavigationProfileMenu avatarSrc={avatarSrc}>
                {showProfileLinks && profileLinks}
              </NavigationProfileMenu>
            )}
          </NavigationSection>
        </Navigation>
      </StickyStackItem>
      {providerId && <ReferProviderModal source={SignOnBonusReferralSource.PROVIDER} providerId={providerId} open={referModalOpen} onClose={() => setReferModalOpen(false)} />}
    </>
  );
};
